import React from 'react';

// Common SVG attributes shared by all nav icons
const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  strokeWidth: '1.8',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

export function AnalyticsIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

// ComponentsIcon uses both stroke (for rects) and fill (for the inner chip dot)
export function ComponentsIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <rect x="2" y="7" width="3" height="10" rx="1" />
      <rect x="19" y="7" width="3" height="10" rx="1" />
      <rect x="7" y="2" width="10" height="3" rx="1" />
      <rect x="7" y="19" width="10" height="3" rx="1" />
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" fill={color} />
    </svg>
  );
}

// Shopping bag icon — more intuitive for consumable accessories (cables, cases, etc.)
// than the archive box it replaced, which was visually identical to IC.archiveBox
// Goods-in QC — lucide `clipboard-check`, per the redesign icon set.
export function QCIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

export function ConsumablesIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

export function DashboardIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

// Truck icon — clearer for logistics dispatch than the previous left-bar + arrow design
export function DispatchIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

// ForecastIcon: calendar with a data row line — distinct from IC.calendar which omits that line
export function ForecastIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="14" x2="16" y2="14" />
    </svg>
  );
}

export function HistoryIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export function LocationsIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export function MaintenanceIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

export function RMAIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <polyline points="9 14 4 9 9 4" />
      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
    </svg>
  );
}

export function SettingsIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// StockIcon: decimal noise in original coords normalised (12.01 → 12, 22.08 → 22)
export function StockIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12 20.73 6.96" />
      <line x1="12" y1="22" x2="12" y2="12" />
    </svg>
  );
}

export function MapPinIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export function TestIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="9 15 11 17 15 13" />
    </svg>
  );
}

// Shifts / Deployment — calendar with clock overlay
export function ShiftsIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

// Flagged on-site — warning triangle
export function FlaggedIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg {...svgProps} width={size} height={size} stroke={color}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
